/**
 * EventsList component for displaying and filtering a list or grid of events
 * Following the pattern of WorkingGroupsList with filters
 */

'use client';

import { Pagination } from '../components/ui/pagination';
import { useEventsFilter } from '../hooks/use-events-filter';
import type { Event } from '../types';
import { EventCard } from '../cards/event-card';
import { EventsFilters } from '../filters/events-filters';
import { SearchIcon } from 'lucide-react';

interface EventsListProps {
  /** Array of events to display */
  events: Event[];
  /** Base URL for event links */
  baseUrl?: string;
  /** Layout variant */
  variant?: 'default' | 'compact' | 'featured';
  /** Show past event indicator */
  showPastIndicator?: boolean;
  /** Page size for pagination */
  pageSize?: number;
  /** Show filters sidebar (default: true) */
  showFilters?: boolean;
  /** Show past events by default (default: false) */
  initialShowPastEvents?: boolean;
  /** Control which filters are displayed */
  enabledFilters?: {
    search?: boolean;
    eventTypes?: boolean;
    themes?: boolean;
    pastEvents?: boolean;
  };
}

/**
 * EventsList component displays a responsive grid of events with filters
 *
 * Features include:
 * - Search filtering by title, location, and description
 * - Event type and theme filters
 * - Show/hide past events toggle
 * - Pagination
 * - Accessible with ARIA live regions
 * - Responsive grid layout
 *
 * @example
 * ```tsx
 * // Basic usage with filters
 * <EventsList
 *   events={events}
 *   baseUrl="/events"
 *   variant="default"
 *   pageSize={12}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Without filters sidebar
 * <EventsList
 *   events={events}
 *   showFilters={false}
 *   variant="compact"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With custom page size
 * <EventsList
 *   events={events}
 *   pageSize={20}
 *   showPastIndicator={false}
 * />
 * ```
 */
export function EventsList({
  events,
  baseUrl = '/events',
  variant = 'default',
  showPastIndicator = true,
  pageSize = 12,
  showFilters = true,
  initialShowPastEvents = false,
  enabledFilters,
}: EventsListProps) {
  const {
    query,
    selectedEventTypes,
    selectedThemes,
    themeFilterMode,
    showPastEvents,
    currentPage,
    eventTypes,
    themes,
    visibleEvents,
    totalResults,
    hasResults,
    hasActiveFilters,
    totalPages,
    startIndex,
    endIndex,
    normalizedQuery,
    setQuery,
    toggleEventType,
    toggleTheme,
    clearEventTypeSelection,
    clearThemeSelection,
    setThemeFilterMode,
    setShowPastEvents,
    handlePageChange,
    clearAllFilters,
  } = useEventsFilter({ events, pageSize, initialShowPastEvents });

  const content = (
    <main className="space-y-6 w-full min-w-0">
      {/* Screen reader announcement for filter changes */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {hasResults
          ? `${totalResults} ${totalResults === 1 ? 'event' : 'events'} found`
          : hasActiveFilters
          ? 'No events match your filters'
          : 'No events available'}
      </div>

      {/* Results Summary */}
      {hasResults && (
        <div className="flex items-center justify-end text-sm text-muted-foreground">
          <p>
            Showing {startIndex + 1}–{endIndex} of {totalResults}{' '}
            {totalResults === 1 ? 'event' : 'events'}
          </p>
        </div>
      )}

      {/* Events Grid */}
      {hasResults ? (
        <>
          <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visibleEvents.map(event => (
              <li key={event.id} className="w-full min-w-0">
                <EventCard
                  event={event}
                  baseUrl={baseUrl}
                  variant={variant}
                  showPastIndicator={showPastIndicator}
                  clickable={false}
                />
              </li>
            ))}
          </ul>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      ) : (
        <div className="flex min-h-[300px] flex-col items-center justify-centre rounded-lg border border-dashed text-center">
          <SearchIcon className="mb-4 h-12 w-12 text-muted-foreground/50" />
          <p className="text-lg font-medium text-muted-foreground">
            {hasActiveFilters ? 'No events match your filters.' : 'No events available.'}
          </p>
        </div>
      )}
    </main>
  );

  if (!showFilters) {
    return <div className="w-full">{content}</div>;
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] w-full max-w-full">
      {/* Events Grid */}
      <div className="lg:col-start-1 w-full min-w-0">{content}</div>

      {/* Filters Sidebar */}
      <aside className="w-full lg:col-start-2 lg:row-start-1 lg:w-80 lg:justify-self-end">
        <EventsFilters
          query={query}
          onQueryChange={setQuery}
          eventTypes={eventTypes}
          selectedEventTypes={selectedEventTypes}
          onToggleEventType={toggleEventType}
          onClearEventTypeSelection={clearEventTypeSelection}
          themes={themes}
          selectedThemes={selectedThemes}
          onToggleTheme={toggleTheme}
          onClearThemeSelection={clearThemeSelection}
          themeFilterMode={themeFilterMode}
          onThemeFilterModeChange={setThemeFilterMode}
          showPastEvents={showPastEvents}
          onShowPastEventsChange={setShowPastEvents}
          totalResults={totalResults}
          hasActiveFilters={hasActiveFilters}
          onClearAllFilters={clearAllFilters}
          enabledFilters={enabledFilters}
        />
      </aside>
    </div>
  );
}
